import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const DEVICE_ID_KEY = 'breth_device_id';
const BEARER_KEY = 'breth_bearer';

// Configure axios to send Authorization header from localStorage as a fallback
// (needed on plain HTTP where the Secure cookie won't be sent)
axios.interceptors.request.use((config) => {
  const tok = localStorage.getItem(BEARER_KEY);
  if (tok && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${tok}`;
  }
  return config;
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    bootstrap();
    // eslint-disable-next-line
  }, []);

  const bootstrap = async () => {
    // Try existing session first
    try {
      const { data } = await axios.get(`${API_URL}/auth/me`, { withCredentials: true });
      setUser(data);
      setLoading(false);
      return;
    } catch (e) {
      if (e?.response?.status && e.response.status !== 401) {
        console.warn('[breth] /auth/me returned unexpected status', e.response.status);
      }
    }
    // No valid session — surface the sign-in screen. Guest is now opt-in
    // (the SignIn screen has a "Continue as Guest" button).
    setUser(false);
    setLoading(false);
  };

  const checkAuth = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch {
      setUser(false);
    }
  };

  const requestMagicLink = async (email) => {
    const { data } = await axios.post(
      `${API_URL}/auth/request-magic-link`,
      { email },
      { withCredentials: true }
    );
    return data;
  };

  const signUpEmail = async ({ email, password, display_name }) => {
    const deviceId = localStorage.getItem(DEVICE_ID_KEY) || undefined;
    const { data } = await axios.post(
      `${API_URL}/auth/signup`,
      { email, password, display_name, device_id: deviceId },
      { withCredentials: true },
    );
    await checkAuth();
    return data;
  };

  const signInEmail = async ({ email, password }) => {
    const { data } = await axios.post(
      `${API_URL}/auth/signin`,
      { email, password },
      { withCredentials: true },
    );
    await checkAuth();
    return data;
  };

  const continueAsGuest = async () => {
    const existingDevice = localStorage.getItem(DEVICE_ID_KEY);
    const url = existingDevice
      ? `${API_URL}/auth/guest?device_id=${encodeURIComponent(existingDevice)}`
      : `${API_URL}/auth/guest`;
    const { data } = await axios.post(url, {}, { withCredentials: true });
    localStorage.setItem(DEVICE_ID_KEY, data.device_id);
    if (data.access_token) localStorage.setItem(BEARER_KEY, data.access_token);
    await checkAuth();
    return data;
  };

  const demoSignIn = async (persona = 'a') => {
    const { data } = await axios.post(
      `${API_URL}/auth/demo-signin?persona=${persona}`,
      {},
      { withCredentials: true }
    );
    if (data.access_token) localStorage.setItem(BEARER_KEY, data.access_token);
    await checkAuth();
    return data;
  };

  const setupProfile = async (displayName) => {
    const { data } = await axios.put(
      `${API_URL}/auth/me`,
      { display_name: displayName },
      { withCredentials: true }
    );
    setUser(data);
    return data;
  };

  const logout = async () => {
    try { await axios.post(`${API_URL}/auth/logout`, {}, { withCredentials: true }); }
    catch (e) { console.warn('[breth] server logout failed, clearing client state anyway', e); }
    localStorage.removeItem(BEARER_KEY);
    localStorage.removeItem(DEVICE_ID_KEY);
    setUser(false);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        requestMagicLink,
        demoSignIn,
        signUpEmail,
        signInEmail,
        continueAsGuest,
        setupProfile,
        logout,
        setUser,
        checkAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
